// Shared searchable staff dropdown (2026-08-25).
//
// Thin adapter over the existing `PremiumSelect` component so every
// admin-facing Staff picker across the app gets the same interaction:
//
//   [ Staff                       ▼ ]
//     ↓ click
//   ┌────────────────────────────┐
//   │ 🔍 Search staff…           │
//   ├────────────────────────────┤
//   │ All Staff                  │
//   │ Dinesh Ratno               │
//   │ Genelle Rodriguez          │
//   │ ...                        │
//   └────────────────────────────┘
//
// Search is client-side against the already-loaded staff list — no
// new API calls, no eligibility scoping change. Callers still pass
// their own `staff` array (filtered by permission / location /
// subject / grade upstream); this component only improves the way
// that list is presented.
//
// Value contract matches `PremiumSelect`: a single string. Callers
// that already stored `staff_id` as `string | ""` need no changes.
// `includeAll` synthesizes an `"" → All Staff` option.

import React, { useMemo } from "react";
import PremiumSelect from "../PremiumSelect";

/** Minimal shape callers must provide per staff entry. Extra fields
 *  are ignored — the component only reads id + display name + avatar. */
export interface StaffOption {
  id: number | string;
  /** Preferred display name. If absent, first+last is used. */
  name?: string;
  teamFirstName?: string;
  teamLastName?: string;
  firstName?: string;
  lastName?: string;
  avatar?: string | null;
  image?: string | null;
  photo?: string | null;
}

interface Props {
  /** Currently-selected staff id as string. Use "" for the All option. */
  value: string;
  onChange: (v: string) => void;
  /** Staff list already loaded by the caller (already scope-filtered). */
  staff: StaffOption[];
  label?: string;
  placeholder?: string;
  /** Prepend an "" → All entry for filters (default true). */
  includeAll?: boolean;
  allLabel?: string;
  disabled?: boolean;
  minWidth?: number;
  /** Show a circular avatar/initial on each row (default true). */
  showAvatar?: boolean;
}

const displayNameFor = (s: StaffOption): string => {
  if (s.name && s.name.trim()) return s.name;
  const first = s.teamFirstName || s.firstName || "";
  const last  = s.teamLastName  || s.lastName  || "";
  const joined = `${first} ${last}`.trim();
  return joined || `Staff #${s.id}`;
};

const avatarFor = (s: StaffOption): string | null | undefined =>
  s.avatar ?? s.image ?? s.photo ?? null;

const StaffSearchSelect: React.FC<Props> = ({
  value, onChange, staff,
  label,
  placeholder = "Select staff…",
  includeAll = true, allLabel = "All Staff",
  disabled = false,
  minWidth = 200,
  showAvatar = true,
}) => {
  const options = useMemo(() => {
    const rows = (Array.isArray(staff) ? staff : []).map((s) => ({
      value: String(s.id),
      label: displayNameFor(s),
      avatar: avatarFor(s),
    }));
    if (includeAll) {
      return [{ value: "", label: allLabel, avatar: null }, ...rows];
    }
    return rows;
  }, [staff, includeAll, allLabel]);

  return (
    <PremiumSelect
      options={options}
      value={value}
      onChange={onChange}
      label={label}
      placeholder={placeholder}
      showAvatar={showAvatar}
      disabled={disabled}
      minWidth={minWidth}
      allowClear={!includeAll}
    />
  );
};

export default StaffSearchSelect;
